import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where, deleteField } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useStock } from '../../hooks/useStock';
import { applyInventoryMovement } from '../../lib/inventoryService';
import { InventoryMovementType } from '../../types/warehouse';
import { generateStockCountPdf, StockCountDoc, StockCountLine } from '../../lib/stockCountPdf';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import BarcodeScanner from './BarcodeScanner';
import {
  ClipboardDocumentCheckIcon, PlusIcon, QrCodeIcon, DocumentArrowDownIcon, PrinterIcon, CheckCircleIcon, ArrowLeftIcon,
  MagnifyingGlassIcon, XMarkIcon, EyeSlashIcon
} from '@heroicons/react/24/outline';

interface CountDoc extends StockCountDoc {
  organizationId: string;
  warehouseId: string;
  blind?: boolean;
  appliedCount?: number;
}

const toDate = (v: any): Date | undefined => (v?.toDate ? v.toDate() : v instanceof Date ? v : undefined);
const fmt = (n: number) => new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 3 }).format(n);
const money = (n: number) => new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(n || 0);
const numberFor = () => { const d = new Date(); const p = (n: number) => String(n).padStart(2, '0'); return `LE-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`; };

/**
 * Leltár: indítás raktáranként (a nyilvántartott mennyiség pillanatképe), számolás telefonon vagy gépen
 * (vonalkóddal +1, kézi beírás, több ember egyszerre – tételenként külön mező), eltérések, lezárás:
 * a számolt mennyiségre korrekciós (STOCK_INVENTORY) mozgás minden eltérő tételre, jegyzőkönyv PDF.
 */
const StockCounts: React.FC = () => {
  const { user } = useAuth();
  const orgId = (user as any)?.organizationId as string | undefined;
  const { warehouses } = useWarehouses();
  const { rows, products } = useStock();
  const [counts, setCounts] = useState<CountDoc[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nWarehouse, setNWarehouse] = useState('');
  const [nScope, setNScope] = useState<'stocked' | 'all'>('stocked');
  const [nBlind, setNBlind] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'stockCounts'), where('organizationId', '==', orgId), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setCounts(snap.docs.map(d => {
      const x = d.data() as any;
      return { id: d.id, ...x, createdAt: toDate(x.createdAt), closedAt: toDate(x.closedAt), lines: x.lines || [], counts: x.counts || {} } as CountDoc;
    })), err => console.error('stockCounts', err));
  }, [orgId]);

  useEffect(() => { if (newOpen) setNWarehouse(warehouses.find(w => (w as any).isDefault)?.id || warehouses[0]?.id || ''); }, [newOpen, warehouses]);

  const start = async () => {
    if (!orgId || !user || !nWarehouse) return;
    setCreating(true);
    try {
      const wh = warehouses.find(w => w.id === nWarehouse);
      const qty = new Map<string, number>();
      rows.filter(r => r.warehouseId === nWarehouse).forEach(r => qty.set(r.productId, (qty.get(r.productId) || 0) + r.quantity));
      const pmap = new Map(products.map(p => [p.id, p]));
      const ids = nScope === 'all'
        ? products.filter(p => p.type !== 'service' && p.isActive !== false).map(p => p.id)
        : Array.from(qty.keys()).filter(id => (qty.get(id) || 0) !== 0);
      const lines: StockCountLine[] = Array.from(new Set(ids)).map(id => {
        const p = pmap.get(id);
        const r = rows.find(x => x.productId === id);
        return { productId: id, code: p?.code || r?.productCode || '', name: p?.name || r?.productName || id, unit: p?.unit || r?.unit || 'db', ...(p?.barcode ? { barcode: p.barcode } : {}), expected: Math.round((qty.get(id) || 0) * 1000) / 1000, ...(p?.purchasePrice != null ? { unitCost: p.purchasePrice } : {}) };
      }).sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      if (!lines.length) { toast.error('Ebben a raktárban nincs leltározható tétel'); setCreating(false); return; }
      const ref = await addDoc(collection(db, 'stockCounts'), {
        organizationId: orgId, number: numberFor(), warehouseId: nWarehouse, warehouseName: wh?.name || '',
        status: 'open', blind: nBlind, lines, counts: {},
        createdAt: serverTimestamp(), createdBy: user.uid, createdByName: (user as any).displayName || user.email || ''
      });
      setNewOpen(false);
      setActiveId(ref.id);
      toast.success(`Leltár elindítva: ${lines.length} tétel`);
    } catch (e: any) { toast.error(e?.message || 'A leltár nem indult el'); }
    finally { setCreating(false); }
  };

  const active = counts.find(c => c.id === activeId) || null;
  if (active) return <CountSheet sc={active} onBack={() => setActiveId(null)} />;

  return (
    <div className="space-y-3">
      <div className="crm-card p-3 flex items-center gap-3">
        <ClipboardDocumentCheckIcon className="w-5 h-5 text-gray-400" />
        <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">Leltár: a nyilvántartott mennyiséget rögzíti, ti megszámoljátok (telefonon vonalkóddal is, akár többen egyszerre), a lezárás a számolt mennyiségre állítja a készletet és jegyzőkönyvet készít.</p>
        <button type="button" onClick={() => setNewOpen(true)} className="crm-btn crm-btn-primary h-8 text-xs flex-shrink-0"><PlusIcon className="w-4 h-4" />Új leltár</button>
      </div>

      {counts.length === 0 ? (
        <div className="crm-card py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          <ClipboardDocumentCheckIcon className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />Még nem volt leltár.
        </div>
      ) : (
        <div className="crm-card overflow-hidden divide-y divide-[var(--crm-border)]">
          {counts.map(c => {
            const done = Object.keys(c.counts || {}).length;
            const diffs = c.lines.filter(l => c.counts[l.productId] != null && c.counts[l.productId] !== l.expected).length;
            return (
              <button key={c.id} type="button" onClick={() => setActiveId(c.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.status === 'open' ? 'bg-amber-500' : c.status === 'closed' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.number} · {c.warehouseName}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{c.createdAt?.toLocaleString('hu-HU')} · {c.createdByName}{c.blind ? ' · vak leltár' : ''}</div>
                </div>
                <div className="text-right text-xs tabular-nums text-gray-600 dark:text-gray-300 flex-shrink-0">
                  <div>{done}/{c.lines.length} számolva</div>
                  <div className={diffs ? 'text-amber-600' : 'text-gray-400'}>{diffs} eltérés</div>
                </div>
                <span className={`text-[11px] px-1.5 h-5 inline-flex items-center rounded ${c.status === 'open' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : c.status === 'closed' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{c.status === 'open' ? 'folyamatban' : c.status === 'closed' ? 'lezárva' : 'megszakítva'}</span>
              </button>
            );
          })}
        </div>
      )}

      <Modal isOpen={newOpen} onClose={() => setNewOpen(false)} title="Új leltár" size="lg">
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Raktár</div>
            <select value={nWarehouse} onChange={e => setNWarehouse(e.target.value)} className="w-full border rounded-lg px-3 h-10">
              {warehouses.filter(w => w.isActive !== false).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Mit leltározunk?</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {([['stocked', 'Ami nyilvántartás szerint itt van', 'gyors, a szokásos'], ['all', 'Minden raktározható termék', 'teljes leltár – előkerült tételekhez']] as const).map(([v, l, s]) => (
                <button key={v} type="button" onClick={() => setNScope(v)} className={`text-left rounded-lg border p-3 ${nScope === v ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-[var(--crm-border)] hover:bg-gray-50 dark:hover:bg-white/5'}`}>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{l}</div><div className="text-xs text-gray-500 dark:text-gray-400">{s}</div>
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
            <input type="checkbox" checked={nBlind} onChange={e => setNBlind(e.target.checked)} className="mt-0.5 w-4 h-4 rounded" />
            <span><b>Vak leltár</b> – a számolók nem látják a nyilvántartott mennyiséget (pontosabb eredmény). Az eltérések a lezárásnál látszanak.</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">A leltár alatt a mozgások (kiadás, bevételezés) rögzíthetők, de a pontosság érdekében érdemes szünetelni őket. Lezáráskor a készlet a számolt mennyiségre áll.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setNewOpen(false)} className="crm-btn crm-btn-ghost">Mégse</button>
            <button type="button" onClick={start} disabled={!nWarehouse || creating} className="crm-btn crm-btn-primary">{creating ? 'Indítás…' : 'Leltár indítása'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/** Egy leltár számolási / ellenőrző nézete. */
const CountSheet: React.FC<{ sc: CountDoc; onBack: () => void }> = ({ sc, onBack }) => {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'all' | 'todo' | 'diff'>('all');
  const [scan, setScan] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showExpected, setShowExpected] = useState(!sc.blind);
  const pending = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [local, setLocal] = useState<Record<string, string>>({});
  const isOpen = sc.status === 'open';

  const setCount = (pid: string, raw: string) => {
    setLocal(l => ({ ...l, [pid]: raw }));
    clearTimeout(pending.current[pid]);
    pending.current[pid] = setTimeout(async () => {
      const v = raw.trim() === '' ? null : Number(raw.replace(',', '.'));
      if (v != null && (isNaN(v) || v < 0)) { toast.error('Érvénytelen mennyiség'); return; }
      try { await updateDoc(doc(db, 'stockCounts', sc.id), { [`counts.${pid}`]: v == null ? deleteField() : v, updatedAt: serverTimestamp() }); }
      catch { toast.error('A mentés nem sikerült'); }
    }, 500);
  };
  const valueOf = (pid: string) => (local[pid] !== undefined ? local[pid] : sc.counts[pid] != null ? String(sc.counts[pid]) : '');

  const onScan = (code: string) => {
    const line = sc.lines.find(l => l.barcode === code || l.code === code);
    if (!line) { toast.error(`Nincs a leltárban: ${code}`); return; }
    const cur = Number(valueOf(line.productId) || 0);
    setCount(line.productId, String(cur + 1));
    toast.success(`${line.name}: ${cur + 1}`, { duration: 1000 });
  };

  const rowsView = useMemo(() => {
    const t = search.trim().toLowerCase();
    return sc.lines.filter(l => {
      if (t && ![l.name, l.code, l.barcode].some(x => (x || '').toLowerCase().includes(t))) return false;
      const c = sc.counts[l.productId];
      if (view === 'todo') return c == null;
      if (view === 'diff') return c != null && c !== l.expected;
      return true;
    });
  }, [sc, search, view]);

  const summary = useMemo(() => {
    let counted = 0, diffs = 0, plus = 0, minus = 0;
    sc.lines.forEach(l => {
      const c = sc.counts[l.productId];
      if (c == null) return;
      counted++;
      const d = c - l.expected;
      if (d !== 0) { diffs++; const v = d * (l.unitCost || 0); if (v > 0) plus += v; else minus += v; }
    });
    return { counted, diffs, plus, minus };
  }, [sc]);

  const close = async () => {
    if (!user) return;
    setConfirmClose(false);
    setClosing(true);
    let applied = 0; const errors: string[] = [];
    for (const l of sc.lines) {
      const c = sc.counts[l.productId];
      if (c == null || c === l.expected) continue;
      try {
        await applyInventoryMovement({
          productId: l.productId, warehouseId: sc.warehouseId, quantity: c, type: InventoryMovementType.STOCK_INVENTORY,
          reason: 'stock_count', stockCountId: sc.id, documentType: 'stock_count', documentNumber: sc.number,
          note: `Leltár ${sc.number}: nyilv. ${fmt(l.expected)} → számolt ${fmt(c)}`
        }, { uid: user.uid, email: user.email, organizationId: sc.organizationId });
        applied++;
      } catch (e: any) { errors.push(`${l.name}: ${e?.message || 'hiba'}`); }
    }
    await updateDoc(doc(db, 'stockCounts', sc.id), {
      status: errors.length ? 'open' : 'closed', appliedCount: applied,
      ...(errors.length ? {} : { closedAt: serverTimestamp(), closedBy: user.uid, closedByName: (user as any).displayName || user.email || '' }),
      updatedAt: serverTimestamp()
    });
    setClosing(false);
    if (errors.length) toast.error(`${applied} korrekció rögzítve, ${errors.length} hiba: ${errors.slice(0, 2).join('; ')}`, { duration: 8000 });
    else toast.success(`Leltár lezárva – ${applied} tétel korrigálva`);
  };

  return (
    <div className="space-y-3">
      <div className="crm-card p-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={onBack} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Vissza"><ArrowLeftIcon className="w-4 h-4" /></button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{sc.number} · {sc.warehouseName}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{summary.counted}/{sc.lines.length} számolva · {summary.diffs} eltérés{showExpected ? ` · többlet ${money(summary.plus)} · hiány ${money(summary.minus)}` : ''}</div>
        </div>
        <button type="button" onClick={() => generateStockCountPdf(sc, { blank: true, blind: !!sc.blind })} className="crm-btn crm-btn-secondary h-8 text-xs" title="Üres leltárív nyomtatáshoz"><PrinterIcon className="w-4 h-4" /><span className="hidden sm:inline">Leltárív</span></button>
        <button type="button" onClick={() => generateStockCountPdf(sc)} className="crm-btn crm-btn-secondary h-8 text-xs"><DocumentArrowDownIcon className="w-4 h-4" /><span className="hidden sm:inline">Jegyzőkönyv</span></button>
        {isOpen && <button type="button" onClick={() => setScan(true)} className="crm-btn crm-btn-secondary h-8 text-xs"><QrCodeIcon className="w-4 h-4" />Vonalkód</button>}
        {isOpen && <button type="button" onClick={() => setConfirmClose(true)} disabled={closing || summary.counted === 0} className="crm-btn crm-btn-primary h-8 text-xs"><CheckCircleIcon className="w-4 h-4" />{closing ? 'Lezárás…' : 'Lezárás'}</button>}
      </div>

      <div className="crm-card p-2.5 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Keresés…" className="crm-input-compact w-full border rounded-md pl-8 pr-2" />
        </div>
        <div className="flex items-center rounded-lg border border-[var(--crm-border)] p-0.5">
          {([['all', 'Mind'], ['todo', 'Hátravan'], ['diff', 'Eltérés']] as const).map(([v, l]) => (
            <button key={v} type="button" onClick={() => setView(v)} className={`h-7 px-2.5 rounded-md text-xs font-medium ${view === v ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300'}`}>{l}</button>
          ))}
        </div>
        {sc.blind && isOpen && (
          <button type="button" onClick={() => setShowExpected(s => !s)} className="crm-btn crm-btn-ghost h-8 text-xs" title="Vak leltár – nyilvántartott mennyiség megjelenítése (ellenőrzéshez)"><EyeSlashIcon className="w-4 h-4" />{showExpected ? 'Elrejt' : 'Nyilv. mutat'}</button>
        )}
      </div>

      <div className="crm-card overflow-hidden divide-y divide-[var(--crm-border)]">
        {rowsView.length === 0 && <div className="py-10 text-center text-sm text-gray-500">Nincs tétel ebben a nézetben.</div>}
        {rowsView.map(l => {
          const raw = valueOf(l.productId);
          const c = raw === '' ? null : Number(raw.replace(',', '.'));
          const diff = c != null && !isNaN(c) ? c - l.expected : null;
          return (
            <div key={l.productId} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{l.name}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{l.code}{l.barcode ? ` · ${l.barcode}` : ''}{showExpected ? ` · nyilv. ${fmt(l.expected)} ${l.unit}` : ''}</div>
              </div>
              {showExpected && diff != null && diff !== 0 && <span className={`text-xs tabular-nums font-medium ${diff > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{diff > 0 ? '+' : ''}{fmt(diff)}</span>}
              {showExpected && diff === 0 && <CheckCircleIcon className="w-4 h-4 text-emerald-500" />}
              <div className="flex items-center gap-1">
                <input value={raw} disabled={!isOpen} onChange={e => setCount(l.productId, e.target.value)} inputMode="decimal" placeholder="—" className="w-20 border rounded-lg px-2 h-10 text-right tabular-nums text-base" />
                <span className="text-xs text-gray-500 w-8">{l.unit}</span>
                {isOpen && raw !== '' && <button type="button" onClick={() => setCount(l.productId, '')} className="text-gray-300 hover:text-rose-600" aria-label="Törlés"><XMarkIcon className="w-4 h-4" /></button>}
              </div>
            </div>
          );
        })}
      </div>

      {isOpen && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setConfirmCancel(true)} className="crm-btn crm-btn-ghost text-xs text-rose-600">Leltár megszakítása</button>
        </div>
      )}

      <BarcodeScanner isOpen={scan} onClose={() => setScan(false)} onDetected={onScan} continuous title="Leltár – vonalkód olvasás" />
      <ConfirmDialog isOpen={confirmClose} title="Leltár lezárása" message={`${summary.diffs} eltérő tétel készlete a számolt mennyiségre áll (többlet ${money(summary.plus)}, hiány ${money(summary.minus)}). ${sc.lines.length - summary.counted} nem számolt tétel változatlan marad. Folytatod?`} confirmText="Lezárás" cancelText="Mégse" type="warning" onConfirm={close} onClose={() => setConfirmClose(false)} />
      <ConfirmDialog isOpen={confirmCancel} title="Leltár megszakítása" message="A leltár megszakad, a készlet nem változik. A számolt adatok megmaradnak a leltárban." confirmText="Megszakítás" cancelText="Vissza" type="danger" onConfirm={async () => { setConfirmCancel(false); await updateDoc(doc(db, 'stockCounts', sc.id), { status: 'cancelled', updatedAt: serverTimestamp() }); }} onClose={() => setConfirmCancel(false)} />
    </div>
  );
};

export default StockCounts;
